// src/seo.tsx
import { createContext, useContext, useEffect } from "react";
import { Fragment, jsx } from "react/jsx-runtime";
var HeadContext = createContext(null);
function HeadProvider({
  children,
  value
}) {
  return /* @__PURE__ */ jsx(HeadContext.Provider, { value, children });
}
function createHeadContext() {
  const heads = [];
  return {
    heads,
    addHead: (props) => {
      heads.push(props);
    }
  };
}
function Head({
  title,
  description,
  keywords,
  ogTitle,
  ogDescription,
  ogImage,
  ogUrl,
  ogType = "website",
  twitterCard = "summary_large_image",
  twitterSite,
  twitterCreator,
  canonical,
  robots = "index, follow",
  children
}) {
  const headContext = useContext(HeadContext);
  if (headContext) {
    headContext.addHead({
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogImage,
      ogUrl,
      ogType,
      twitterCard,
      twitterSite,
      twitterCreator,
      canonical,
      robots
    });
  }
  if (typeof window !== "undefined") {
    useEffect(() => {
      if (title) {
        document.title = title;
      }
      const updateOrCreateMeta = (name, content) => {
        if (!content) return;
        let meta = document.querySelector(
          `meta[name="${name}"], meta[property="${name}"]`
        );
        if (!meta) {
          meta = document.createElement("meta");
          if (name.startsWith("og:") || name.startsWith("twitter:")) {
            meta.setAttribute("property", name);
          } else {
            meta.setAttribute("name", name);
          }
          document.head.appendChild(meta);
        }
        meta.content = content;
      };
      updateOrCreateMeta("description", description);
      updateOrCreateMeta("keywords", keywords);
      updateOrCreateMeta("robots", robots);
      updateOrCreateMeta("og:title", ogTitle || title);
      updateOrCreateMeta("og:description", ogDescription || description);
      updateOrCreateMeta("og:image", ogImage);
      updateOrCreateMeta("og:url", ogUrl);
      updateOrCreateMeta("og:type", ogType);
      updateOrCreateMeta("twitter:card", twitterCard);
      updateOrCreateMeta("twitter:site", twitterSite);
      updateOrCreateMeta("twitter:creator", twitterCreator);
      if (canonical) {
        let link = document.querySelector(
          'link[rel="canonical"]'
        );
        if (!link) {
          link = document.createElement("link");
          link.rel = "canonical";
          document.head.appendChild(link);
        }
        link.href = canonical;
      }
    }, [
      title,
      description,
      keywords,
      ogTitle,
      ogDescription,
      ogImage,
      ogUrl,
      ogType,
      twitterCard,
      twitterSite,
      twitterCreator,
      canonical,
      robots
    ]);
  }
  return /* @__PURE__ */ jsx(Fragment, { children });
}
function renderHeadToString(heads) {
  const merged = heads.reduce((acc, head) => ({ ...acc, ...head }), {});
  const tags = [];
  if (merged.title) {
    tags.push(`<title>${escapeHtml(merged.title)}</title>`);
  }
  if (merged.description) {
    tags.push(`<meta name="description" content="${escapeHtml(merged.description)}">`);
  }
  if (merged.keywords) {
    tags.push(`<meta name="keywords" content="${escapeHtml(merged.keywords)}">`);
  }
  if (merged.robots) {
    tags.push(`<meta name="robots" content="${escapeHtml(merged.robots)}">`);
  }
  if (merged.ogTitle || merged.title) {
    tags.push(
      `<meta property="og:title" content="${escapeHtml(merged.ogTitle || merged.title || "")}">`
    );
  }
  if (merged.ogDescription || merged.description) {
    tags.push(
      `<meta property="og:description" content="${escapeHtml(merged.ogDescription || merged.description || "")}">`
    );
  }
  if (merged.ogImage) {
    tags.push(`<meta property="og:image" content="${escapeHtml(merged.ogImage)}">`);
  }
  if (merged.ogUrl) {
    tags.push(`<meta property="og:url" content="${escapeHtml(merged.ogUrl)}">`);
  }
  if (merged.ogType) {
    tags.push(`<meta property="og:type" content="${escapeHtml(merged.ogType)}">`);
  }
  if (merged.twitterCard) {
    tags.push(`<meta name="twitter:card" content="${escapeHtml(merged.twitterCard)}">`);
  }
  if (merged.twitterSite) {
    tags.push(`<meta name="twitter:site" content="${escapeHtml(merged.twitterSite)}">`);
  }
  if (merged.twitterCreator) {
    tags.push(`<meta name="twitter:creator" content="${escapeHtml(merged.twitterCreator)}">`);
  }
  if (merged.canonical) {
    tags.push(`<link rel="canonical" href="${escapeHtml(merged.canonical)}">`);
  }
  return tags.join("\n    ");
}
function escapeHtml(str) {
  return str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}
var seo_default = Head;

export {
  HeadContext,
  HeadProvider,
  createHeadContext,
  Head,
  renderHeadToString,
  seo_default
};
//# sourceMappingURL=chunk-VIAW3MUX.js.map